/**
 * The public header.
 *
 * Deliberately not `TopNav`: that one is the signed-in application chrome and
 * links to /home, /browse and a profile switcher, none of which exist for a
 * visitor. This one navigates the marketing page itself and offers the two
 * doors into the product.
 *
 * The small-screen menu is a disclosure, not an overlay trap: a real button
 * carrying `aria-expanded`, closed by Escape, by a link activation, and by a
 * click outside. Focus is never moved for the visitor, so a keyboard user
 * simply continues into the panel that has just appeared.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/landing/BrandMark";

const SECTIONS = [
  { href: "#watch", label: "Watch" },
  { href: "#plans", label: "Plans" },
  { href: "#creators", label: "Creators" },
  { href: "#advertisers", label: "Advertisers" },
  { href: "#questions", label: "Questions" },
];

export function LandingHeader() {
  const [open, setOpen] = useState(false);
  const [lifted, setLifted] = useState(false);
  const header = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setLifted(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <header ref={header} className={lifted ? "lheader lheader--lifted" : "lheader"}>
      <a className="lheader__skip" href="#main">
        Skip to content
      </a>

      <div className="lheader__bar">
        <a href="#top" className="lheader__brand">
          <BrandMark size={26} />
          <span>Hayfish</span>
        </a>

        <nav className="lheader__nav" aria-label="Sections of this page">
          {SECTIONS.map((section) => (
            <a key={section.href} href={section.href}>
              {section.label}
            </a>
          ))}
        </nav>

        <div className="lheader__actions">
          <Link to="/signin" className="btn btn--quiet lheader__signin">
            Sign in
          </Link>
          {/* "Create account", not "Start free trial": the hero owns that phrase, and
              two links with one accessible name is a worse link list for everyone. */}
          <Link to="/signup" className="btn btn--accent">
            Create account
          </Link>
          <button
            type="button"
            className="lheader__toggle"
            aria-expanded={open}
            aria-controls="landing-menu"
            onClick={() => setOpen((value) => !value)}
          >
            <span className="visually-hidden">{open ? "Close menu" : "Open menu"}</span>
            <span className="lheader__bars" aria-hidden="true" data-open={open} />
          </button>
        </div>
      </div>

      <div className="lheader__menu" id="landing-menu" hidden={!open}>
        <nav aria-label="Sections of this page, compact">
          {SECTIONS.map((section) => (
            <a key={section.href} href={section.href} onClick={() => setOpen(false)}>
              {section.label}
            </a>
          ))}
          <Link to="/signin" onClick={() => setOpen(false)}>
            Sign in
          </Link>
        </nav>
      </div>
    </header>
  );
}
